import Phaser from 'phaser';
import type { CharacterDirection } from '../characters/CharacterArt';
import { CUSTOMER_VISUAL_IDS, type CustomerVisualId } from './CustomerVisualCatalog';
export { CUSTOMER_VISUAL_IDS, customerVisualForSequence, visualStateForCustomer } from './CustomerVisualCatalog';
export type { CustomerVisualId } from './CustomerVisualCatalog';

interface AnimationDefinition {
  frames: number[];
  frameRate: number;
  repeat: number;
  mirrorX?: boolean;
}

interface CustomerManifest {
  customerVisualId: CustomerVisualId;
  animations: Record<string, AnimationDefinition>;
}

const DIRECTIONAL_STATES = new Set(['idle', 'arrive', 'walk', 'queueIdle', 'leaving']);

export function preloadProductionCustomers(scene: Phaser.Scene): void {
  for (const customerId of CUSTOMER_VISUAL_IDS) {
    const base = `${import.meta.env.BASE_URL}assets/customers/${customerId}`;
    scene.load.spritesheet(`customer-${customerId}`, `${base}/atlas.png`, { frameWidth: 192, frameHeight: 192 });
    scene.load.json(`customer-${customerId}-manifest`, `${base}/manifest.json`);
  }
}

export function registerProductionCustomerAnimations(scene: Phaser.Scene): void {
  for (const customerId of CUSTOMER_VISUAL_IDS) {
    const manifest = scene.cache.json.get(`customer-${customerId}-manifest`) as CustomerManifest | undefined;
    if (!manifest) continue;
    for (const [semanticKey, definition] of Object.entries(manifest.animations)) {
      const key = `customer.${customerId}.${semanticKey}`;
      if (scene.anims.exists(key)) continue;
      scene.anims.create({
        key,
        frames: definition.frames.map((frame) => ({ key: `customer-${customerId}`, frame })),
        frameRate: definition.frameRate,
        repeat: definition.repeat,
      });
    }
  }
}

export function createProductionCustomerSprite(
  scene: Phaser.Scene,
  visualId: CustomerVisualId,
  scale = 0.78,
  baselineY = 34,
): Phaser.GameObjects.Sprite {
  const sprite = scene.add.sprite(0, baselineY, `customer-${visualId}`, 0)
    .setOrigin(0.5, 184 / 192)
    .setScale(scale);
  sprite.setData('customerVisualId', visualId).setData('artScale', scale);
  const initial = `customer.${visualId}.arrive.down`;
  if (scene.anims.exists(initial)) sprite.play(initial);
  return sprite;
}

function resolveKey(
  scene: Phaser.Scene,
  visualId: CustomerVisualId,
  state: string,
  direction: CharacterDirection,
): { key?: string; flipX: boolean } {
  const directional = DIRECTIONAL_STATES.has(state);
  const artDirection = direction === 'left' && directional ? 'right' : direction;
  const candidates = directional
    ? [`customer.${visualId}.${state}.${artDirection}`, `customer.${visualId}.${state}.down`]
    : [`customer.${visualId}.${state}`, `customer.${visualId}.idle.down`];
  return { key: candidates.find((candidate) => scene.anims.exists(candidate)), flipX: directional && direction === 'left' };
}

export function setProductionCustomerState(
  container: Phaser.GameObjects.Container,
  state: string,
  direction?: CharacterDirection,
  ignoreIfPlaying = false,
): boolean {
  const sprite = container.getData('sprite') as Phaser.GameObjects.Sprite | undefined;
  const visualId = sprite?.getData('customerVisualId') as CustomerVisualId | undefined;
  if (!sprite || !visualId) return false;
  const resolvedDirection = direction ?? container.getData('direction') ?? 'down';
  const { key, flipX } = resolveKey(container.scene, visualId, state, resolvedDirection);
  if (!key) return false;
  container.setData('customerVisualState', state).setData('direction', resolvedDirection);
  sprite.setFlipX(flipX);
  if (!ignoreIfPlaying || sprite.anims.currentAnim?.key !== key) sprite.play(key, true);
  return true;
}

export function setProductionCustomerDirection(container: Phaser.GameObjects.Container, direction: CharacterDirection): boolean {
  const sprite = container.getData('sprite') as Phaser.GameObjects.Sprite | undefined;
  if (!sprite?.getData('customerVisualId')) return false;
  return setProductionCustomerState(container, container.getData('customerVisualState') ?? 'idle', direction, true);
}

